import { PageLayout } from 'src/layouts/page'
import { Tabs, Card, Breadcrumb, Image, LinkButton, NotFound, PendingBookingProfileBanner, Text, Skeleton, Tooltip } from '$components'
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { BinIcon, DogIcon, EditIcon } from '$components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ElementIntent } from '$components'
import { petDeleteMutation, petShowOptions } from '$api-client/@tanstack/react-query.gen'
import { PetRoutePaths } from '$features/pets/routes/protected'
import { Router } from '$utils/router'
import { OptionsDropdown } from '$components'
import { ConfirmationModal } from '$components'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { Role } from '$api-client/types.gen'
import { BreadcrumbSkeleton } from '$components'
import { ProfileSkeleton } from '$components'
import { InfoSkeleton } from '$components'
import { TabsSkeleton } from '$components'
import { useModalState } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { useDocumentTitle } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function PetPage() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { hasSubscriptionExpired } = useTenant()

  const activeTab = location.pathname.split('/').pop()

  const {
    modalState: petDeleteConfirmationModalState,
    open: openPetDeleteConfirmationModal,
    close: closePetDeleteConfirmationModal,
  } = useModalState()

  const { data, isLoading: isPetLoading } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
    retry: false,
  })

  const { mutate: deletePet, isPending: isDeletingPet } = useMutation({
    ...petDeleteMutation(),
    onSuccess: () => {
      navigate(PetRoutePaths.Pets)
      ToastHelper.deleteSuccess(ToastType.Pet)
    },
    onError: () => ToastHelper.deleteError(ToastType.Pet),
  })

  const handleConfirmDelete = () => {
    deletePet({
      path: {
        pet: petDeleteConfirmationModalState.modelId,
      },
    })
  }

  const pet = data?.data

  useDocumentTitle({
    title: pet ? `${pet.name} - Pet Details` : 'Pet Details',
  })

  if (isPetLoading) {
    return (
      <PageLayout>
        <div className="flex flex-col gap-stack-section w-full">
          <BreadcrumbSkeleton />

          {/* Header Skeleton */}
          <div className="flex items-center gap-stack-roomy">
            <ProfileSkeleton
              avatarSize={64}
              titleWidth={160}
              subtitleWidth={120}
            />
            <div className="flex items-center gap-stack-tight ml-auto">
              <Skeleton
                width={140}
                height={36}
                borderRadius={10}
              />
              <Skeleton
                width={36}
                height={36}
                borderRadius={10}
              />
            </div>
          </div>

          {/* Tabs Skeleton */}
          <TabsSkeleton
            count={4}
            widths={[100, 160, 160, 100]}
          />

          {/* Content Skeleton */}
          <div className="flex flex-col gap-stack-section">
            {/* Parent Information Card Skeleton */}
            <Card
              rounding="xl"
              padding="none"
            >
              <Card.Header
                padding="sm"
                className="flex justify-between items-center"
              >
                <Skeleton
                  width={160}
                  height={20}
                  borderRadius={7}
                />
                <Skeleton
                  width={64}
                  height={28}
                  borderRadius={8}
                />
              </Card.Header>

              <Card.Content
                padding="sm"
                className="flex flex-col gap-stack-roomy"
              >
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
              </Card.Content>
            </Card>

            {/* Pet Information Card Skeleton */}
            <Card
              rounding="xl"
              padding="none"
            >
              <Card.Header
                padding="sm"
                className="flex justify-between items-center"
              >
                <Skeleton
                  width={160}
                  height={20}
                  borderRadius={7}
                />
                <Skeleton
                  width={64}
                  height={28}
                  borderRadius={8}
                />
              </Card.Header>

              <Card.Content
                padding="sm"
                className="flex flex-col gap-stack-roomy"
              >
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
                <InfoSkeleton
                  labelWidth={80}
                  contentWidth={200}
                />
              </Card.Content>
            </Card>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!pet) { return <NotFound /> }

  return (
    <PageLayout>
      <Breadcrumb
        items={[
          {
            label: 'Pets',
            path: PetRoutePaths.Pets,
            icon: DogIcon,
          },
          {
            label: pet.name,
            path: PetRoutePaths.PetsView,
          },
        ]}
        className="mb-stack-section"
      />

      {!pet.approved_at && (
        <PendingBookingProfileBanner className="mb-stack-section" />
      )}

      {/* Main Content */}
      <div className="flex flex-col gap-stack-section w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-stack-roomy">
          <div className="flex items-center gap-stack-roomy">
            <Image
              size="lg"
              src={pet.image}
              alt={pet.name}
              fallbackIcon={DogIcon}
            />

            <div className="flex items-center gap-stack-roomy">
              <div className="flex flex-col relative">
                <Text
                  as="h1"
                  type="h2"
                >
                  {pet.name}
                </Text>

                <Text type="s">
                  {pet.breed.join(', ')}
                </Text>
              </div>

              {pet.approved_at && (
                <Tooltip
                  content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
                  disabled={!hasSubscriptionExpired}
                >
                  <LinkButton
                    variant="secondary"
                    size="sm"
                    to={Router.buildRoute(
                      activeTab!,
                      PetRoutePaths.PetsNameEdit,
                    )}
                    icon={EditIcon}
                    disabled={hasSubscriptionExpired}
                  >
                    Edit
                  </LinkButton>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="flex items-center gap-stack-tight sm:ml-auto">
            {pet.approved_at && (
              <>
                <Tooltip
                  content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
                  disabled={!hasSubscriptionExpired}
                >
                  <LinkButton
                    variant="secondary"
                    to={Router.buildRoute(activeTab!, PetRoutePaths.PetsCreateAppointment)}
                    className="w-full sm:w-fit"
                    state={{ pet, client: pet?.parent }}
                    disabled={hasSubscriptionExpired}
                  >
                    Schedule Appointment
                  </LinkButton>
                </Tooltip>

                <OptionsDropdown options={[
                  {
                    id: 'delete',
                    value: 'Delete',
                    onClick: () => openPetDeleteConfirmationModal(pet.id),
                    intent: ElementIntent.Danger,
                    icon: BinIcon,
                    disabled: !!pet.deleted_at || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
                    tooltip: pet.deleted_at
                      ? 'This pet has already been deleted.'
                      : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                        ? 'You are not allowed to delete pets.'
                        : undefined,
                  },
                ]}
                />
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs variant="pill">
          <Tabs.Tab to={PetRoutePaths.PetsDetails}>
            Details
          </Tabs.Tab>

          <Tabs.Tab to={PetRoutePaths.PetsAppointmentHistory}>
            Appointment History
          </Tabs.Tab>

          <Tabs.Tab to={PetRoutePaths.PetsMedicalInformation}>
            Medical Information
          </Tabs.Tab>

          <Tabs.Tab to={PetRoutePaths.PetsNotes}>
            Notes
          </Tabs.Tab>

          {/* <Tabs.Tab to={PetRoutePaths.PetsGallery}>
            Gallery
          </Tabs.Tab> */}
        </Tabs>

        <div className="mb-stack-roomy">
          <Outlet />
        </div>
      </div>

      {petDeleteConfirmationModalState.isOpen && (
        <ConfirmationModal
          isLoading={isDeletingPet}
          intent={ElementIntent.Danger}
          onClose={closePetDeleteConfirmationModal}
          onConfirm={handleConfirmDelete}
          title="Delete Pet"
          description="Are you sure you want to delete this pet?"
        />
      )}
    </PageLayout>
  )
}
